import React, { useState } from 'react';

import useTreatmentPlanMasterStore from '../../../stores/treatmentPlanMasterStore';

/**
 * SaveAsCombo — heart-icon button at the bottom of the half-disc widget.
 *
 * Snapshots the current visit's sittings into a new TreatmentPlan master
 * via existing `POST /treatment-plans`. Single-line name prompt, then
 * fire-and-forget. No full StepEditor on this surface — for nuanced
 * authoring (per-step approval, default executor / approver, ordering
 * rule, package discount) the doctor still uses /org/treatment-plans.
 *
 * Props:
 *   sittings  — current Sitting[] for this visit
 *   palette   — disc theme tokens
 *   onSaved() — fires after successful save
 */
export default function SaveAsCombo({ sittings, palette, onSaved }) {
  const createPlan = useTreatmentPlanMasterStore((s) => s.createPlan);
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const eligibleSittings = (sittings || []).filter((s) => {
    // Only include non-terminal sittings — saving a "cancelled" or
    // "completed" sitting as a template step makes no sense.
    return !['cancelled'].includes(s.status);
  });

  const canSave = eligibleSittings.length > 0;

  const handleSave = async () => {
    if (!canSave || !name.trim() || busy) return;
    setBusy(true);
    setError('');

    const steps = eligibleSittings.map((s, idx) => ({
      step_number: idx + 1,
      procedure_id: s.procedure_id || s.procedure_snapshot?.procedure_id,
      is_mandatory: s.is_mandatory !== false,
      requires_approval: !!s.requires_approval,
      discount_amount: '0',
      estimated_cost_override:
        s.estimated_cost ? String(s.estimated_cost) : null,
    }));

    const body = {
      plan_name: name.trim(),
      steps,
      ordering_rule: 'flexible',
      package_discount: '0',
    };

    const r = await createPlan(body);
    setBusy(false);
    if (r?.success) {
      setOpen(false);
      setName('');
      if (typeof onSaved === 'function') await onSaved(r);
    } else {
      setError(r?.error || 'Could not save plan.');
    }
  };

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        disabled={!canSave}
        title={canSave ? 'Save these procedures as a reusable patient plan' : 'Add a procedure first'}
        className="inline-flex items-center gap-1.5 rounded-lg border font-bold
                   bg-teal-50 text-teal-800 border-teal-300 hover:bg-teal-100 disabled:opacity-50
                   dark:bg-teal-900/40 dark:text-teal-100 dark:border-teal-700 dark:hover:bg-teal-900/60"
        style={{ fontSize: '0.6875rem', padding: '0.45rem 0.75rem' }}
      >
        <span aria-hidden="true">♥</span>
        <span>Save as plan</span>
      </button>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.375rem' }}>
      <label htmlFor="save-combo-name" style={{ fontSize: '0.6875rem', opacity: 0.85 }}>
        Plan name
      </label>
      <input
        id="save-combo-name"
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder="e.g. RCT + Crown"
        maxLength={120}
        autoFocus
        style={{
          width: '100%',
          padding: '0.4rem 0.625rem',
          borderRadius: 8,
          border: `1px solid ${palette.border}`,
          background: palette.bg,
          color: palette.text,
          fontSize: '0.8125rem',
          outline: 'none',
        }}
      />
      {error && (
        <p style={{ margin: 0, fontSize: '0.6875rem', color: '#b91c1c' }}>
          {error}
        </p>
      )}
      <div style={{ display: 'flex', gap: '0.375rem' }}>
        <button
          type="button"
          onClick={handleSave}
          disabled={busy || !name.trim()}
          className="flex-1 rounded-lg text-white text-xs font-bold py-1.5
                     disabled:opacity-60 disabled:cursor-not-allowed"
          style={{ background: 'linear-gradient(135deg,#0d9488,#14b8a6)', boxShadow: '0 6px 14px -8px rgba(13,148,136,0.85)' }}
        >
          {busy ? 'Saving…' : 'Save plan'}
        </button>
        <button
          type="button"
          onClick={() => { setOpen(false); setName(''); setError(''); }}
          className="px-3 rounded-lg border bg-white text-slate-700 hover:bg-slate-100
                     dark:bg-slate-800 dark:text-slate-100 dark:hover:bg-slate-700
                     dark:border-slate-600"
          style={{ fontSize: '0.6875rem', borderColor: palette.border }}
        >
          Cancel
        </button>
      </div>
    </div>
  );
}
